import { createContext, ReactNode, use, useMemo } from "react";
import {
  ChemicalElementSymbol,
  MaterialRead,
  NonElementQuantity,
  StrategyDashboardHeatData,
  useGetMaterialMetadataSetQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { sorted } from "helpers";
import { useHeatsForTimeRange } from "hooks/usePrecachedHeats";
import { useHeatsTimeRange } from "./HeatsTimeRangeContext";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useTenantData } from "contexts/tenant";

export interface HeatsDataContextType {
  data: StrategyDashboardHeatData | undefined;
  materials: MaterialRead[] | undefined;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  hasLifts: boolean;
  hasBaskets: boolean;
  scrapEyeOnly: boolean;
  hasPriceComparison: boolean;
  availableElements: (ChemicalElementSymbol | NonElementQuantity)[];
}

export const HeatsDataContext = createContext<HeatsDataContextType | undefined>(
  undefined
);

const getMaterialNamesWithNonzeroMass = (
  data: StrategyDashboardHeatData
): Set<string> => {
  const names = new Set<string>();
  for (const heat of data.series) {
    for (const mix of heat.mixes) {
      for (const total of mix.mix.totals) {
        if (total.material_name && total.mass > 0) {
          names.add(total.material_name);
        }
      }
    }
  }
  return names;
};

interface HeatsDataProviderProps {
  children: ReactNode;
}

export const HeatsDataProvider = ({ children }: HeatsDataProviderProps) => {
  const tenantName = useTenant();
  const {
    timeRange,
    day,
    prefetchRanges,
    filterMode,
    isLoading: navIsLoading,
    isError: navIsError,
  } = useHeatsTimeRange();

  const {
    data,
    isFetching: isHeatsFetching,
    isError: isHeatsError,
    isLoading: isHeatsLoading,
  } = useHeatsForTimeRange({
    timeRange,
    day,
    prefetchRanges,
    filterMode,
  });

  const {
    data: materialsData,
    isError: isMaterialsError,
    isLoading: isMaterialsLoading,
  } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });

  const sortedMaterials = useMemo(() => {
    if (!data || !materialsData) {
      return undefined;
    }
    const nonzeroMass = getMaterialNamesWithNonzeroMass(data);
    return sorted(
      materialsData.materials.filter(
        (m) => m.visibility === "visible" || nonzeroMass.has(m.name)
      ),
      (a) => a.ordering
    );
  }, [data, materialsData]);

  const hasLifts = useIsBehindFeatureFlag("scrap_eyes");

  const { recipe_type } = useTenantData();
  const hasBaskets = recipe_type == "bucket";

  const availableElements = useMemo(
    () => data?.available_sample_constraints ?? [],
    [data]
  );

  const scrapEyeOnly = Object.keys(
    data?.available_mix_summaries ?? {}
  ).includes("liftStrikes");

  const hasPriceComparison = Object.keys(
    data?.available_summaries ?? {}
  ).includes("scrapEyePriceDiff");

  const hasData = data !== undefined;
  const isFetching = isHeatsFetching || (!hasData && navIsLoading);
  const isLoading =
    isHeatsLoading || isMaterialsLoading || (!hasData && navIsLoading);
  const isError = isHeatsError || isMaterialsError || navIsError;

  const value: HeatsDataContextType = useMemo(
    () => ({
      data,
      materials: sortedMaterials,
      isFetching,
      isLoading,
      isError,
      hasLifts,
      hasBaskets,
      scrapEyeOnly,
      hasPriceComparison,
      availableElements,
    }),
    [
      data,
      sortedMaterials,
      isFetching,
      isLoading,
      isError,
      hasLifts,
      hasBaskets,
      scrapEyeOnly,
      hasPriceComparison,
      availableElements,
    ]
  );

  return <HeatsDataContext value={value}>{children}</HeatsDataContext>;
};

export const useHeatsData = (): HeatsDataContextType => {
  const context = use(HeatsDataContext);
  if (context === undefined) {
    throw new Error("useHeatsData must be used within a HeatsDataProvider");
  }
  return context;
};
